import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Editor from "../../components/elements/tiptap-editor/Editor";
import Button from "../../components/form/Button";
import FileUploadSingle from "../../components/form/FileUploadSingle";
import UrlInput from "../../components/form/UrlInput";
import { classNames } from "../../helpers/classNames";
import DragDropfile from "../form/DragDropfile";
import DeleteModal from "../common/DeleteModal";

const TextEditor = ({
  onChange = () => {},
  value = [],
  className,
  labelClasses,
  label,
  required,
  errorType,
  errorText,
  format = ".jpg, .png, .jpeg",
  onRemove = () => {},
}) => {
  const [nodes, setNodes] = useState([
    { id: "1", type: "text", value: "" },
    { id: "2", type: "image", value: "" },
    { id: "3", type: "link", value: "" },
  ]);

  useEffect(() => {
    if (value?.length === 0 && nodes?.length > 0) {
      onChange(nodes);
    }
  }, []);

  useEffect(() => {
    // if (value?.length > 0) {
    setNodes(value);
    // }
  }, [JSON.stringify(value)]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reordered = Array.from(nodes);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    setNodes(reordered);
    onChange(reordered);
  };

  const addNode = (type) => {
    const update_node = [
      ...nodes,
      {
        id: Date.now().toString(),
        type,
        value: "",
      },
    ];
    setNodes(update_node);
    onChange(update_node);
  };

  const onEditorValueChange = (newValue, index) => {
    const updatedNodes = [...nodes];
    updatedNodes[index].value = newValue;
    setNodes(updatedNodes);
    onChange(updatedNodes);
  };

  function onNodeDeleteHandler(node) {
    if (node?.file instanceof File) {
      const updatedNodes = [...nodes]?.filter(
        (item, index) => item?.id !== node.id
      );
      setNodes(updatedNodes);
      onChange(updatedNodes);
    } else {
      // setShowDeleteModal({ isOpen: true, data: node });
      onRemove(node);
    }
  }

  return (
    <div className="relative w-full">
      {label && (
        <div
          className={
            "text-xs font-medium text-text-disabled mb-1 font-CanelaDeck " +
            labelClasses
          }
        >
          {label}
          {required && (
            <span className="text-[8px] text-red-500 ml-1 absolute top-[-2px]">
              <i className="fa-solid fa-asterisk"></i>
            </span>
          )}
        </div>
      )}
      <div
        className={classNames("space-y-5 bg-white rounded-md p-3", className)}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="droppable-nodes">
            {(provided) => (
              <div
                className={classNames("space-y-3")}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {nodes.map((node, i) => (
                  <Draggable key={node.id} draggableId={node.id} index={i}>
                    {(provided) => (
                      <div
                        className="flex flex-col md:flex-row items-center gap-3 group"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div
                          {...provided.dragHandleProps}
                          className="opacity-0 group-hover:opacity-100"
                        >
                          <i className="fa-fw fa-solid fa-grip-dots-vertical cursor-move"></i>
                        </div>
                        <div className="flex-grow w-full">
                          {node?.type === "text" && (
                            <Editor
                              height={200}
                              value={node?.value}
                              onChange={(val) => {
                                onEditorValueChange(val, i);
                              }}
                            />
                          )}
                          {node?.type === "image" && (
                            <DragDropfile
                              value={node?.value}
                              formats={format}
                              isMultiple={false}
                              previewClasses={"!w-full !max-w-96 !h-auto"}
                              handleFile={(val) => {
                                onEditorValueChange(val?.[0], i);
                              }}
                              removeButton={false}
                            />
                          )}
                          {node?.type === "link" && (
                            <UrlInput
                              value={node?.value}
                              placeholder="Input your video link here"
                              onChange={(e) => {
                                onEditorValueChange(e.target.value, i);
                              }}
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          className="w-8 h-8 group-hover:opacity-100 flex items-center justify-center text-red-500 hover:!opacity-65 transition-opacity duration-300 opacity-0"
                          onClick={() => {
                            onNodeDeleteHandler(node);
                          }}
                        >
                          <i className="fa-fw fa-regular fa-trash-can"></i>
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="flex flex-wrap gap-3 md:gap-auto items-center justify-around">
          <Button
            buttonIcon={"fa-regular fa-file-lines"}
            variant="secondary"
            buttonLabel="Add Text"
            buttonFunction={() => addNode("text")}
            buttonClasses={"w-full md:w-auto"}
          />
          <Button
            buttonIcon={"fa-regular fa-image"}
            variant="secondary"
            buttonLabel="Add Image"
            buttonFunction={() => addNode("image")}
            buttonClasses={"w-full md:w-auto"}
          />
          <Button
            buttonIcon={"fa-regular fa-video"}
            variant="secondary"
            buttonLabel="Add Video"
            buttonFunction={() => addNode("link")}
            buttonClasses={"w-full md:w-auto"}
          />
        </div>
      </div>
      {errorType && errorText && (
        <div
          className={classNames(
            "text-xs mt-1",
            errorType === "error" && "text-red-600",
            errorType === "warning" && "text-amber-600",
            errorType === "success" && "text-green-600",
            errorType === "info" && "text-sky-600"
          )}
        >
          <i
            className={classNames(
              "fa-regular fa-fw mr-1",
              errorType === "error" && "fa-square-exclamation",
              errorType === "warning" && "fa-triangle-exclamation",
              errorType === "success" && "fa-circle-check",
              errorType === "info" && "fa-circle-info"
            )}
          ></i>
          {errorText}
        </div>
      )}
    </div>
  );
};

export default TextEditor;
